/**
 * La règle d'une date, d'une heure ou d'un nombre tapé au clavier (chantier `bugs`, ticket 01).
 *
 * Pendant la frappe, le champ de date du navigateur passe par 0002, 0020, 0202 avant 2026, et
 * le champ de nombre par 1 avant 10 : chacune de ces valeurs est « valide » pour lui. On ne les
 * écrit donc qu'à la sortie du champ, et seulement entières : une année avant 1900 ou après
 * 2199 est une année qu'on est encore en train de taper, pas une date qu'on veut poser.
 *
 * Vide est complet : c'est retirer la valeur. Sauf quand le champ n'est vide que parce qu'il
 * est à moitié rempli (le navigateur le dit, `incomplet`) : alors on rend la valeur d'avant,
 * jamais un effacement.
 */

import { ANNEES_POSSIBLES, BORNES_BULLE } from '@nextilt/commun';

export type SorteDeSaisie = 'jour' | 'instant' | 'nombre';
/** Les bornes d'un nombre, comprises ; `pas` : l'écart entre deux valeurs permises, 1 par défaut. */
export type Bornes = { min: number; max: number; pas?: number };

export type IssueDeLaSaisie = { sorte: 'ecrire'; valeur: string } | { sorte: 'rien' } | { sorte: 'revenir' };

/**
 * La durée d'un rendez-vous, tapée dans « Quand » : de 1 minute à 24 heures, à la minute près
 * (décision d'Igor du 2026-10-07) ; les bornes du serveur, lues dans `commun/`.
 */
export const BORNES_DE_LA_DUREE: Bornes = { min: BORNES_BULLE.dureeMinMin, max: BORNES_BULLE.dureeMinMax, pas: 1 };

/** Le délai du monde, tapé dans « Quand » : de 0 à dix ans, dans son unité. */
export function bornesDuDelai(enMois: boolean): Bornes {
  return { min: 0, max: enMois ? BORNES_BULLE.delaiMoisMax : BORNES_BULLE.delaiJoursMax };
}

/** Un jour AAAA-MM-JJ qui existe au calendrier, dans des années plausibles. */
function jourReel(jour: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour);
  if (!m) return false;
  const [annee, mois, quantieme] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (annee < ANNEES_POSSIBLES.premiere || annee > ANNEES_POSSIBLES.derniere) return false;
  const d = new Date(Date.UTC(annee, mois - 1, quantieme));
  return d.getUTCFullYear() === annee && d.getUTCMonth() === mois - 1 && d.getUTCDate() === quantieme;
}

/** La valeur peut-elle s'écrire telle quelle ? */
export function estComplete(sorte: SorteDeSaisie, valeur: string, bornes?: Bornes): boolean {
  if (valeur === '') return true;
  switch (sorte) {
    case 'jour':
      return jourReel(valeur);
    case 'instant': {
      const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(valeur);
      return !!m && jourReel(m[1]!) &&Number(m[2]) < 24 && Number(m[3]) < 60;
    }
    case 'nombre': {
      if (!/^\d+$/.test(valeur)) return false;
      const n = Number(valeur);
      return !bornes || (n >= bornes.min && n <= bornes.max && (n - bornes.min) % (bornes.pas ?? 1) === 0);
    }
  }
}

/** Ce qu'on fait en quittant le champ : écrire, ne rien faire, ou rendre la valeur d'avant. */
export function issueDeLaSaisie(
  sorte: SorteDeSaisie,
  valeur: string,
  avant: string,
  bornes?: Bornes,
  incomplet = false
): IssueDeLaSaisie {
  if (incomplet || !estComplete(sorte, valeur, bornes)) return { sorte: 'revenir' };
  if (valeur === avant) return { sorte: 'rien' };
  return { sorte: 'ecrire', valeur };
}

/**
 * Quitter une ligne ouverte de la fiche l'enregistre et la range (deuxième retour d'Igor du
 * 2026-10-03, chantier `revue-design`, ticket 02 : plus de bouton « Fini »). Le focus qui part
 * ailleurs, ou nulle part (un clic sur le fond, un toucher ailleurs), la quitte ; le focus qui
 * passe à un autre champ de la même ligne (le rendez-vous et sa durée, qui et son numéro) non.
 */
export function quitteLaLigne(ligne: { contains: (n: never) => boolean }, suivant: unknown): boolean {
  return suivant === null || !ligne.contains(suivant as never);
}

/**
 * Entrée enregistre et range (retour d'Igor du 2026-10-03, chantier `revue-design`, ticket 02) :
 * dans un champ d'une ligne (`input`), elle valide et range ce qui s'était ouvert. Jamais dans une zone de texte (les notes : Entrée y passe à la ligne), ni sur un
 * bouton (Entrée le clique déjà), ni pendant une composition au clavier (un accent en cours).
 */
export function entreeRange(touche: string, balise: string, enComposition: boolean): boolean {
  return touche === 'Enter' && balise === 'INPUT' && !enComposition;
}
